import { useNavigate } from "react-router";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../../components/modal/Dialog";
import { useOrderRulesGetStats } from "../../../../hooks/api/v2/orderRules/useOrderRulesGetStats";
import { useState } from "react";
import { useOrderRulesDeleteAndReassign } from "../../../../hooks/api/v2/orderRules/useOrderRulesDeleteAndReassign";
import { useOrderRulesList } from "../../../../hooks/api/v2/orderRules/useOrderRulesList";
import { useForm } from "react-hook-form";
import { InputLabel, InputContainer, InputSelect } from "@sellar-io/components";
import { Button } from "@sellar-io/components";
import { OrderRulesListResponseType } from "@sellar-io/api-v2/types";
import { apiV2AssertError } from "../../../../hooks/api/v2/apiV2Request";
import * as ROUTES from "../../../../constants/routes";
import { Alert } from "../../../../components/alert/Alert";

type OrderRulesDeleteDialogProps = {
  orderRulesId: number;
  orderRulesName: string;
  active: boolean;
  setActive: (active: boolean) => void;
};

export const OrderRulesDeleteDialog = ({
  orderRulesId,
  orderRulesName,
  active,
  setActive,
}: OrderRulesDeleteDialogProps) => {
  const orderRulesStatsQuery = useOrderRulesGetStats(orderRulesId, {
    enabled: Boolean(orderRulesId),
  });

  const customersDirectlyAssignedCount =
    orderRulesStatsQuery.data?.customersDirectlyAssignedCount ?? 0;

  const hasEntitiesToReassign = customersDirectlyAssignedCount > 0;

  const description = hasEntitiesToReassign
    ? `You can choose to assign a different set of order rules to the customers currently assigned '${orderRulesName}'`
    : `Are you sure you want to delete '${orderRulesName}' order rules`;

  return (
    <>
      <Dialog active={active} onDismiss={() => setActive(false)}>
        <DialogNavigation
          leftIcon="CHEVRON_DOWN"
          onClickLeftIcon={() => setActive(false)}
          title={`Delete order rules set?`}
          description={description}
        />
        <DialogContent>
          {active && (
            <Content
              orderRulesId={orderRulesId}
              setActive={setActive}
              hasEntitiesToReassign={hasEntitiesToReassign}
              customersDirectlyAssignedCount={customersDirectlyAssignedCount}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const Content = ({
  orderRulesId,
  setActive,
  hasEntitiesToReassign,
  customersDirectlyAssignedCount,
}: {
  orderRulesId: number;
  setActive: (active: boolean) => void;
  hasEntitiesToReassign: boolean;
  customersDirectlyAssignedCount: number;
}) => {
  const navigate = useNavigate();
  const [submissionErrorMessage, setSubmissionErrorMessage] = useState("");
  const orderRulesDeleteAndReassignMutation =
    useOrderRulesDeleteAndReassign(orderRulesId);

  const orderRulesListQuery = useOrderRulesList(
    {},
    {
      enabled: Boolean(orderRulesId),
    },
  );

  const handleDeleteAndReassign = (
    data: OrderRulesDeleteAndReassignFormValues,
  ) => {
    const newOrderRulesId =
      data.newOrderRulesIdForDirectlyAssignedCustomers === 0
        ? null
        : data.newOrderRulesIdForDirectlyAssignedCustomers;

    orderRulesDeleteAndReassignMutation.mutate(
      {
        directlyAssignedCustomers: {
          oldOrderRulesId: orderRulesId,
          newOrderRulesId,
        },
      },
      {
        onSuccess: () => {
          setActive(false);
          navigate(ROUTES.ORDER_RULES);
        },
        onError: (error) => {
          const message = apiV2AssertError(error)?.response?.data.message ?? "";
          setSubmissionErrorMessage(message);
        },
      },
    );
  };

  const orderRulesList = orderRulesListQuery.data?.pages[0] ?? [];

  const isSubmitting = false;

  return (
    <>
      {hasEntitiesToReassign ? (
        <OrderRulesDeleteAndReassignForm
          customersDirectlyAssignedCount={customersDirectlyAssignedCount}
          orderRulesList={orderRulesList}
          currentOrderRulesId={orderRulesId}
          isSubmitting={isSubmitting}
          errorMessage={submissionErrorMessage}
          onSubmit={handleDeleteAndReassign}
        />
      ) : (
        <div className="flex justify-end">
          <Button
            color="red"
            disabled={isSubmitting}
            loading={isSubmitting}
            onClick={() => {
              handleDeleteAndReassign({
                customerZones: [],
                newOrderRulesIdForDirectlyAssignedCustomers: null,
              });
            }}
          >
            Delete
          </Button>
        </div>
      )}
    </>
  );
};

type OrderRulesDeleteAndReassignFormProps = {
  orderRulesList: OrderRulesListResponseType;
  currentOrderRulesId: number;
  customersDirectlyAssignedCount: number;
  isSubmitting: boolean;
  errorMessage: string;
  onSubmit: (data: OrderRulesDeleteAndReassignFormValues) => void;
};

type OrderRulesDeleteAndReassignFormValues = {
  customerZones: Array<{
    customerZoneId: number;
    newOrderRulesId: number;
  }>;
  newOrderRulesIdForDirectlyAssignedCustomers: number | null;
};

const OrderRulesDeleteAndReassignForm = ({
  orderRulesList,
  currentOrderRulesId,
  customersDirectlyAssignedCount,
  isSubmitting,
  errorMessage,
  onSubmit,
}: OrderRulesDeleteAndReassignFormProps) => {
  const form = useForm<OrderRulesDeleteAndReassignFormValues>({
    defaultValues: {
      customerZones: [],
    },
  });

  const filteredOrderRulesList = orderRulesList.filter(
    (priceTier) => priceTier.id !== currentOrderRulesId,
  );

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="flex flex-col gap-4"
    >
      {errorMessage && (
        <Alert type="error" message={errorMessage} className="w-full" />
      )}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <InputLabel>{`Select order rules for ${customersDirectlyAssignedCount} individually assigned customers`}</InputLabel>
          <InputContainer>
            <InputSelect
              {...form.register("newOrderRulesIdForDirectlyAssignedCustomers", {
                setValueAs: (value) => Number(value),
              })}
            >
              {filteredOrderRulesList.map((rule) => (
                <option key={rule.id} value={rule.id}>
                  {rule.name}
                </option>
              ))}
            </InputSelect>
          </InputContainer>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button
          color="transparent"
          type="button"
          onClick={() => {
            form.setValue("newOrderRulesIdForDirectlyAssignedCustomers", null);
            form.handleSubmit(onSubmit)();
          }}
        >
          Skip
        </Button>
        <Button disabled={isSubmitting} loading={isSubmitting} type="submit">
          Delete and Assign
        </Button>
      </div>
    </form>
  );
};
